// Tenant dashboard. KPI tiles + compliance posture land in later C1.x. PLAN-C1.9 + PLAN-P6 (deadlines).
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../lib/session";
import { useI18n } from "../lib/i18n";
import { humanizeEnum } from "../lib/enum-labels";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function DashboardRoute() {
  const session = useSession();
  const { t } = useI18n();
  return (
    <section className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("dash.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{session.user.email}</p>
      </div>

      <DeadlinesPanel />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <NavCard title={t("dash.verwerkingen.title")} body={t("dash.verwerkingen.body")} to="/verwerkingen" />
        <NavCard title={t("dash.tickets.title")} body={t("dash.tickets.body")} to="/tickets" />
      </div>
    </section>
  );
}

// PLAN-P6 — upcoming/overdue compliance deadlines (DSAR Art.12(3), PDCA) surfaced on the cockpit.
function DeadlinesPanel() {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["deadlines"], queryFn: () => api.listDeadlines() as Promise<Array<Record<string, unknown>>> });
  const rows = data ?? [];
  return (
    <Card className="max-w-2xl">
      <CardHeader><CardTitle>{t("dash.deadlines.title")}</CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 && <p className="text-sm text-muted-foreground">{t("dash.deadlines.none")}</p>}
        {rows.length > 0 && (
          <ul className="flex flex-col">
            {rows.map((r, i) => {
              const overdue = Boolean(r.overdue);
              const due = r.dueAt ? new Date(String(r.dueAt)).toLocaleDateString() : "—";
              return (
                <li key={i} className="flex items-center gap-2.5 border-b border-border/60 py-2 text-sm last:border-0">
                  <Badge variant="secondary" className="shrink-0 bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300">{humanizeEnum(r.type)}</Badge>
                  <Link to={String(r.link ?? "/")} className="min-w-0 flex-1 truncate text-foreground hover:underline">{String(r.label)}</Link>
                  <span className={overdue ? "font-semibold text-destructive" : "text-muted-foreground"}>
                    {due}{overdue ? ` · ${t("dash.deadlines.overdue")}` : ""}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function NavCard({ title, body, to }: { title: string; body: string; to: string }) {
  return (
    <Link to={to} className="block transition-colors">
      <Card size="sm" className="h-full transition-shadow hover:shadow-md">
        <CardContent className="flex flex-col gap-1.5">
          <span className="font-medium">{title}</span>
          <span className="text-sm text-muted-foreground">{body}</span>
        </CardContent>
      </Card>
    </Link>
  );
}
